// SAD-857: the program glossary as the UI reads it, and the one matcher that
// finds its terms in any string. Built once per glossary (memoized on the
// query's data), it compiles every printed term into a single regular
// expression; each string's split is cached, so a table re-render costs a
// Map lookup per cell.
/** The section the model pass writes (baltigo glossaryread.AcronymsKey). */ export const INFERRED_SECTION = 'acronyms';
/** pangaea's glossary.Norm: lowercase letters and digits only. */ export function termKey(value) {
    return value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}
/**
 * Two expansions name the same thing once case, punctuation, "&"/"and" and a
 * plural "s" are set aside (baltigo definedterms.SameSense).
 */ export function senseKey(value) {
    return value.replace(/&/g, ' and ').replace(/['\u2019]/g, '').toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean).map((w)=>w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w).join(' ');
}
/** Groups the read model's term rows into entries, one per term. */ export function buildEntries(terms) {
    const byKey = new Map();
    for (const t of terms){
        const key = termKey(t.term);
        if (!key) continue;
        let entry = byKey.get(key);
        if (!entry) {
            entry = {
                key,
                term: t.term,
                senses: [],
                conflict: false
            };
            byKey.set(key, entry);
        }
        const refs = t.refs.map((r)=>({
                page: r.page,
                quote: r.quote,
                documentId: r.documentId,
                sectionKey: r.sectionKey
            }));
        const sk = senseKey(t.expansion);
        const same = entry.senses.find((s)=>senseKey(s.expansion) === sk);
        if (same) {
            same.ids.push(t.id);
            if (!same.printings.includes(t.term)) same.printings.push(t.term);
            same.refs.push(...refs);
            same.inferred = same.inferred && refs.every((r)=>r.sectionKey === INFERRED_SECTION);
            continue;
        }
        entry.senses.push({
            ids: [
                t.id
            ],
            term: t.term,
            printings: [
                t.term
            ],
            expansion: t.expansion,
            kind: t.kind,
            refs,
            inferred: refs.length > 0 && refs.every((r)=>r.sectionKey === INFERRED_SECTION)
        });
    }
    const out = [
        ...byKey.values()
    ];
    for (const e of out){
        // A meaning read from use stands in only where no document defines the
        // term: once one does, the document's printing is the meaning (an
        // inference from another document is no second meaning to weigh).
        const defined = e.senses.filter((s)=>!s.inferred);
        if (defined.length && defined.length < e.senses.length) {
            // The inferred meaning goes, but its printings still mark the term.
            for (const s of e.senses)if (s.inferred) {
                for (const p of s.printings)if (!defined[0].printings.includes(p)) defined[0].printings.push(p);
            }
            e.senses = defined;
        }
        // A meaning with no words is no meaning to compare.
        e.conflict = new Set(e.senses.filter((s)=>s.expansion).map((s)=>senseKey(s.expansion))).size > 1;
        // Defined meanings first, then by how often the documents cite them.
        e.senses.sort((a, b)=>Number(a.inferred) - Number(b.inferred) || b.refs.length - a.refs.length);
    }
    return out.sort((a, b)=>a.term.localeCompare(b.term));
}
const SPLIT_CACHE_LIMIT = 5000;
function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
}
/**
 * The matcher for a glossary. A term matches as printed (case-sensitive: "IT"
 * never matches "it"), as a whole word ("SSP" not in "SSPR"), with a plural
 * or possessive "s" ("SSPs", "SSP's"). Longer terms win ("COR" over "CO").
 */ export function buildMatcher(entries) {
    const byKey = new Map();
    const keyOfPrinted = new Map();
    const printedOf = new Map();
    for (const e of entries){
        byKey.set(e.key, e);
        for (const s of e.senses){
            for (const raw of s.printings){
                const p = raw.trim();
                if (p.length < 2) continue;
                keyOfPrinted.set(p, e.key);
                const lower = p.toLowerCase();
                const set = printedOf.get(lower) ?? new Set();
                set.add(p);
                printedOf.set(lower, set);
            }
        }
    }
    const alts = [
        ...keyOfPrinted.keys()
    ].sort((a, b)=>b.length - a.length).map(escapeRegExp);
    const re = alts.length ? new RegExp(`(?<![\\p{L}\\p{N}])(${alts.join('|')})(?:'s|’s|s)?(?![\\p{L}\\p{N}])`, 'gu') : null;
    const cache = new Map();
    const split = (text)=>{
        if (!re || !text || !/\p{Lu}/u.test(text)) return [
            {
                text
            }
        ];
        const hit = cache.get(text);
        if (hit) return hit;
        const out = [];
        let last = 0;
        re.lastIndex = 0;
        for(let m = re.exec(text); m; m = re.exec(text)){
            const key = keyOfPrinted.get(m[1]);
            if (!key) continue;
            if (m.index > last) out.push({
                text: text.slice(last, m.index)
            });
            out.push({
                text: m[0],
                key
            });
            last = m.index + m[0].length;
        }
        if (last < text.length) out.push({
            text: text.slice(last)
        });
        if (out.length === 0) out.push({
            text
        });
        if (cache.size >= SPLIT_CACHE_LIMIT) cache.clear();
        cache.set(text, out);
        return out;
    };
    const printed = (word)=>{
        const set = printedOf.get(word.toLowerCase());
        return set && set.size === 1 ? [
            ...set
        ][0] : undefined;
    };
    return {
        entries: byKey,
        split,
        printed
    };
}
